import React, { useEffect, useState } from "react";
import api from "../api/axiosConfig";
import endpoints from "../api/endpoints";
import { blogImageSrc } from "../api/blogImage";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";

const PAGE_SIZE = 20;

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function BlogsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Math.max(
    parseInt(searchParams.get("page") || "1", 10) || 1,
    1
  );
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(requestedPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadBlogs() {
      setLoading(true);
      try {
        const res = await api.get(endpoints.BLOGS, {
          params: { page: requestedPage, limit: PAGE_SIZE },
        });
        if (cancelled) return;
        setBlogs(res.data.blogs || []);
        setPage(res.data.page || requestedPage);
        setTotalPages(res.data.totalPages || 0);
      } catch (err) {
        console.error("ERROR LOADING BLOGS:", err);
        if (!cancelled) {
          setBlogs([]);
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBlogs();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      cancelled = true;
    };
  }, [requestedPage]);

  const goToPage = (nextPage) => {
    const next = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (next === requestedPage) return;
    if (next <= 1) {
      setSearchParams({});
      return;
    }
    setSearchParams({ page: String(next) });
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb title="Blogs" />

      {/* Blog Section */}
      <section className="blog-section">
        <div className="container">
          <h2 className="blog-section-title">Latest Blogs</h2>

          {/* LOADING STATE */}
          {loading && (
            <div className="empty-state">
              <p>Loading blogs...</p>
            </div>
          )}

          {/* EMPTY STATE */}
          {!loading && blogs.length === 0 && (
            <div className="empty-state">
              <p>No blogs found.</p>
            </div>
          )}

          {/* BLOG LIST */}
          {!loading && blogs.length > 0 && (
            <div className="row g-4">
              {blogs.map((blog) => (
                <div
                  className="col-lg-4 col-md-6 col-sm-12"
                  key={blog._id}
                >
                  <div className="blog-card h-100">
                    <img
                      src={blogImageSrc(blog.image)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = blogImageSrc();
                      }}
                      alt={blog.title}
                      style={{
                        height: "220px",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />

                    <div className="blog-content">
                      <h5 className="card-title">{blog.title}</h5>
                      <p className="card-text">
                        {blog.description}
                      </p>

                      <Link
                        to={`/blog/${blog.slug}`}
                        className="theme-btn-sm"
                      >
                        Read More →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <nav className="blog-pagination" aria-label="Blog pages">
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>

              {pageItems(page, totalPages).map((item) =>
                typeof item === "string" ? (
                  <span key={item} className="page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={item === page ? "active" : undefined}
                    onClick={() => goToPage(item)}
                    aria-current={item === page ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default BlogsList;
